import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  Search, 
  Filter, 
  X, 
  ChevronDown,
  Calendar,
  Users,
  FileText,
  SortAsc,
  SortDesc
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientSearchFilters = ({ onSearch, loading }) => {
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    oficiales: [],
    vendedores: [],
    visa_types: [],
    stages: []
  });
  
  const [filters, setFilters] = useState({
    search: '',
    stage: '',
    oficial_id: '',
    vendedor_id: '',
    visa_type: '',
    has_pending_docs: '',
    date_from: '',
    date_to: '',
    sort_by: 'created_at',
    sort_order: 'desc'
  });

  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    // Count active filters
    let count = 0;
    if (filters.stage) count++;
    if (filters.oficial_id) count++;
    if (filters.vendedor_id) count++;
    if (filters.visa_type) count++;
    if (filters.has_pending_docs !== '') count++;
    if (filters.date_from) count++;
    if (filters.date_to) count++;
    setActiveFiltersCount(count);
  }, [filters]);

  const fetchFilterOptions = async () => {
    try {
      const res = await axios.get(`${API}/admin/clients/filter-options`);
      setFilterOptions(res.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const handleSearch = () => {
    const params = {
      search: filters.search || null,
      stage: filters.stage ? parseInt(filters.stage) : null,
      oficial_id: filters.oficial_id || null,
      vendedor_id: filters.vendedor_id || null,
      visa_type: filters.visa_type || null,
      has_pending_docs: filters.has_pending_docs === '' ? null : filters.has_pending_docs === 'true',
      date_from: filters.date_from || null,
      date_to: filters.date_to || null,
      sort_by: filters.sort_by,
      sort_order: filters.sort_order,
      page: 1,
      limit: 20
    };
    onSearch(params);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      stage: '',
      oficial_id: '',
      vendedor_id: '',
      visa_type: '',
      has_pending_docs: '',
      date_from: '',
      date_to: '',
      sort_by: 'created_at',
      sort_order: 'desc'
    });
    onSearch({ page: 1, limit: 20, sort_by: 'created_at', sort_order: 'desc' });
  };

  const toggleSortOrder = () => {
    const newOrder = filters.sort_order === 'desc' ? 'asc' : 'desc';
    setFilters({ ...filters, sort_order: newOrder });
  };

  return (
    <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
      {/* Search Bar */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-400" />
          <Input
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            onKeyPress={handleKeyPress}
            placeholder="Buscar por nombre, email o teléfono..."
            className="pl-10 h-11"
            data-testid="client-search-input"
          />
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className={`h-11 ${activeFiltersCount > 0 ? 'border-emerald-500 text-emerald-600' : ''}`}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filtros
            {activeFiltersCount > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full">
                {activeFiltersCount}
              </span>
            )}
          </Button>
          
          <Button
            onClick={handleSearch}
            disabled={loading}
            className="h-11 bg-emerald-600 hover:bg-emerald-700"
            data-testid="search-btn"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Search className="w-4 h-4 mr-2" />
                Buscar
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Expanded Filters */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pt-4 mt-4 border-t border-navy-100">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Stage Filter */}
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1">
                    Etapa del Proceso
                  </label>
                  <select
                    value={filters.stage}
                    onChange={(e) => setFilters({ ...filters, stage: e.target.value })}
                    className="w-full px-3 py-2 border border-navy-200 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                  >
                    <option value="">Todas las etapas</option>
                    {filterOptions.stages.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>

                {/* Oficial Filter */}
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1">
                    Oficial Asignado
                  </label>
                  <select
                    value={filters.oficial_id}
                    onChange={(e) => setFilters({ ...filters, oficial_id: e.target.value })}
                    className="w-full px-3 py-2 border border-navy-200 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                  >
                    <option value="">Todos los oficiales</option>
                    {filterOptions.oficiales.map((o) => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                </div>

                {/* Vendedor Filter */}
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1">
                    Vendedor Asignado
                  </label>
                  <select
                    value={filters.vendedor_id}
                    onChange={(e) => setFilters({ ...filters, vendedor_id: e.target.value })}
                    className="w-full px-3 py-2 border border-navy-200 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                  >
                    <option value="">Todos los vendedores</option>
                    {filterOptions.vendedores.map((v) => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                </div>

                {/* Visa Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1">
                    Tipo de Visa
                  </label>
                  <select
                    value={filters.visa_type}
                    onChange={(e) => setFilters({ ...filters, visa_type: e.target.value })}
                    className="w-full px-3 py-2 border border-navy-200 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                  >
                    <option value="">Todos los tipos</option>
                    {filterOptions.visa_types.map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </div>

                {/* Pending Docs Filter */}
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1">
                    Documentos Pendientes
                  </label>
                  <select
                    value={filters.has_pending_docs}
                    onChange={(e) => setFilters({ ...filters, has_pending_docs: e.target.value })}
                    className="w-full px-3 py-2 border border-navy-200 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                  >
                    <option value="">Todos</option>
                    <option value="true">Con pendientes</option>
                    <option value="false">Sin pendientes</option>
                  </select>
                </div>

                {/* Date From */}
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1">
                    Fecha Desde
                  </label>
                  <Input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                    className="h-10"
                  />
                </div>

                {/* Date To */}
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1">
                    Fecha Hasta
                  </label>
                  <Input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                    className="h-10"
                  />
                </div>

                {/* Sort By */}
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1">
                    Ordenar Por
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={filters.sort_by}
                      onChange={(e) => setFilters({ ...filters, sort_by: e.target.value })}
                      className="flex-1 px-3 py-2 border border-navy-200 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                    >
                      <option value="created_at">Fecha de registro</option>
                      <option value="name">Nombre</option>
                      <option value="progress">Progreso</option>
                      <option value="stage">Etapa</option>
                      <option value="pending_docs">Docs pendientes</option>
                    </select>
                    <button
                      onClick={toggleSortOrder}
                      className="px-3 py-2 border border-navy-200 rounded-lg hover:bg-navy-50"
                    >
                      {filters.sort_order === 'desc' ? (
                        <SortDesc className="w-4 h-4" />
                      ) : (
                        <SortAsc className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Clear Filters */}
              {activeFiltersCount > 0 && (
                <div className="mt-4 flex justify-end">
                  <Button
                    variant="ghost"
                    onClick={clearFilters}
                    className="text-navy-500 hover:text-navy-700"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Limpiar filtros
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ClientSearchFilters;
